import {
	findRaisedRegEdRound,
	getRegEdConditionsPending,
	IRegEdSubmissionMetadata,
	IRegEdSubmissionView,
	RegEdComplianceContentType,
	useAccountUsesRegEd,
	useRegEdEmailRegenerateCompliancePdfMutation,
	useRegEdRevisionFiling,
	useRegEdEmailReleaseMutation,
	useRegEdEmailReviseMutation,
	useRegEdMetadataSchemaQuery,
	useRegEdSocialPostReleaseMutation,
} from '@Queries/RegEdCompliance';
import {
	CampaignViewModel,
	EmailSendStatus,
	IImpersonationContext,
	ImpersonationBroker,
	IEmailMessageFollowUpOptions,
	ScheduleCriteria,
	SendEmailFrom,
} from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'sonner';
import { CampaignType } from '../../../../models/AdminModels';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../../models';
import { IUserSessionComponentProps, UserSessionViewModelKey } from '../../../../models/AppState';
import { EmailComposerContext } from '../../../../models/Email';
import { useErrorMessages, useFullscreenModal } from '../../../../models/hooks/appStateHooks';
import { useLocalNotificationService } from '../../../../models/LocalNotifications';
import { Topics } from '../../../../models/LocalNotificationTopics';
import { useEventLogging } from '../../../../models/Logging';
import { getDisplayName } from '../../../../models/NameUtils';
import {
	accountAllowsComplianceBypass,
	accountHasComplianceApplied,
	accountHasComplianceEnabled,
	carrySendForward,
	getAccountComplianceEmail,
	getDefaultDateStringValue,
	getNonSendingDaysOfWeek,
} from '../../../../models/UiUtils';
import { SocialAccountCampaignViewModel } from '../../../../viewmodels/AdminViewModels';
import {
	ComposeEmailViewModel,
	IOperationResultNoValue,
	PostStatus,
	SocialMediaPostViewModel,
} from '../../../../viewmodels/AppViewModels';
import { Button as ButtonV2 } from '../../../components-v2/Button';
import { Dialog, DialogDescription, DialogHeader, DialogPopup, DialogTitle } from '../../../components-v2/Dialog';
import { baseStyleSheet } from '../../../styles/styles';
import { Button } from '../../Button';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../ConfirmationDialog';
import { buildRegEdSocialReviseLocation } from '../../compliance/regEdReviseSocial';
import { findReviewMinimumDate } from '../../compliance/RegEdMetadataForm/regEdMetadataFormAdapter';
import { RegEdComplianceApprovalPrompt } from '../ComplianceApprovalPrompt/RegEdComplianceApprovalPrompt';
import { CampaignDateTimePicker } from '../CampaignDateTimePicker';
import { RegEdComplianceStatus } from '../../compliance/RegEdComplianceStatus';
import { RegEdReviseDateNotice, getRegEdReviseDateState } from '../../compliance/RegEdReviseDateNotice';
import { RegEdRevisionOwedCard } from '../../compliance/RegEdRevisionOwedCard';
import { TagEmailTagList } from '../../email/TagEmailTagList';
import { ITestEmailResult, TestEmailModal } from '../../email/TestEmailModal';
import { PaperPlaneIcon } from '../../svgs/icons/PaperPlaneIcon';
import { WarningIcon } from '../../svgs/icons/WarningIcon';
import { ApprovalStatus, ComplianceSendActions, ScheduleBy, ScheduleDate } from './presentation';
import { styleSheet } from './styles';

export const lastUsedComplianceRecipientEmail = 'lastUsedComplianceRecipientEmail';
export const getCancelConfirmationOptions = (
	destructiveOptionText: string,
	cancelText = 'Cancel'
): IConfirmationDialogOption<boolean>[] => {
	return [
		{
			isDestructive: true,
			representedObject: true,
			title: destructiveOptionText,
		},
		{
			isCancel: true,
			representedObject: false,
			title: cancelText,
		},
	];
};
interface IProps extends IUserSessionComponentProps, IImpersonationContextComponentProps {
	campaign?: CampaignViewModel | SocialMediaPostViewModel;
	campaignGroup?: CampaignViewModel[];
	className?: string;
	disabled?: boolean;
	emailComposer?: ComposeEmailViewModel<IEmailMessageFollowUpOptions>;
	onApproveClicked?(): void;
	onApproveWithoutCompliance?(): void;
	onCancelClicked?(e?: React.MouseEvent<HTMLElement>): void;
	onChangeScheduleClicked?(): void;
	onEditRecipientsClicked?(): void;
	onViewCitationsClicked?(): void;
	onSendCompliance?(sendWithComplianceEmail?: string): void;
	/**
	 * Host-owned revise, for content whose save this component cannot reach. Email keeps the sequence
	 * here because its composer arrives as a prop; a post's save lives in its editor.
	 */
	onReviseCompliance?(submission: IRegEdSubmissionView): void | Promise<void>;
	/**
	 * Supplied by a host that owns the revision sequence itself (social). Its in-flight and owed-filing
	 * state has to reach this panel, or a revision driven from here runs with no guard and no retry.
	 */
	isRevising?: boolean;
	isRetryingRevision?: boolean;
	onRetryRevisionClicked?(): void;
	revisionOwed?: { needsFiling: boolean } | null;
	readOnly?: boolean;
	styles?: StyleDeclarationValue[];
}

const CampaignDetailsSummaryFc: React.FC<IProps> = props => {
	const {
		campaign,
		campaignGroup,
		className,
		disabled,
		emailComposer,
		impersonationContext,
		onApproveClicked,
		onApproveWithoutCompliance,
		onCancelClicked,
		onChangeScheduleClicked,
		onEditRecipientsClicked,
		onSendCompliance,
		onReviseCompliance,
		isRevising: hostIsRevising,
		isRetryingRevision: hostIsRetryingRevision,
		onRetryRevisionClicked: hostOnRetryRevisionClicked,
		revisionOwed: hostRevisionOwed,
		onViewCitationsClicked,
		readOnly,
		styles,
		userSession,
	} = props;

	const fullscreenModal = useFullscreenModal();
	const { logApiError, logEvent } = useEventLogging('CampaignDetailsSummary');
	const { postNotification } = useLocalNotificationService<SocialMediaPostViewModel>();
	const { sendTestEmail } = React.useContext(EmailComposerContext) || {};
	const [showingTestEmailModal, setShowingTestEmailModal] = React.useState(false);
	const onTestEmailModalClose = (result?: ITestEmailResult, canceled?: boolean) => {
		setShowingTestEmailModal(false);
		if (result?.success && !canceled) {
			toast.success('Test email sent successfully!');
		}
	};
	const errorMessages = useErrorMessages();
	const [showingCancelConfirmation, setShowingCancelConfirmation] = React.useState<boolean>(false);
	const [showingReviseConfirmation, setShowingReviseConfirmation] = React.useState<boolean>(false);
	// The revision is reopened but nothing is filed until the PDF renders, and no server field tells us
	// that happened — so the retry offer is held here for the advisor who is still on the screen.
	// What a revision still owes, so the retry resumes at the step that failed rather than re-running
	// the last one — which would render a second PDF against an unrepointed submission and report success.
	// A compliance round outlives the date it was scheduled against, and ApproveAsync refuses a campaign
	// whose start date is more than two days old — so a revision that keeps the stale date is one the
	// reviewer cannot approve, and the error lands on them rather than the advisor who could fix it.
	const [reviseSendDate, setReviseSendDate] = React.useState<Date | null>(null);
	const [pickingReviseDate, setPickingReviseDate] = React.useState<boolean>(false);
	// The date picker replaces the questionnaire in the dialog, and the prompt seeds once per mount —
	// without holding the draft here, opening the picker would re-seed from the prior round's filing and
	// discard whatever the advisor had typed.
	const [reviseDraft, setReviseDraft] = React.useState<IRegEdSubmissionMetadata | null>(null);
	// The date a stale send was carried forward from, kept so the dialog can say it moved.
	const [reviseCarriedFrom, setReviseCarriedFrom] = React.useState<Date | null>(null);
	const reviseCarryPending = React.useRef(false);
	const [isRevising, setIsRevising] = React.useState<boolean>(false);
	// A host that drives the sequence itself owns this state; this panel only owns it for email.
	const effectiveIsRevising = hostIsRevising ?? isRevising;
	const [pendingRevision, setPendingRevision] = React.useState<IRegEdSubmissionView | null>(null);
	// RegEd accounts suppress the internal approve/reject blocks in favor of the read-only RegEd panel.
	const usesRegEd = useAccountUsesRegEd(impersonationContext);
	const history = useHistory();
	const regEdEmailRevise = useRegEdEmailReviseMutation({
		onError: error => errorMessages.pushApiError(error),
	});
	const regEdRegeneratePdf = useRegEdEmailRegenerateCompliancePdfMutation({
		onError: error => errorMessages.pushApiError(error),
	});

	const regEdEmailRelease = useRegEdEmailReleaseMutation({
		onError: error => errorMessages.pushApiError(error),
		onSuccess: () => {
			toast.success('Released — this campaign will send as scheduled.');
			return campaign?.load()?.catch(error => errorMessages.pushApiError(error));
		},
	});
	const regEdSocialRelease = useRegEdSocialPostReleaseMutation({
		onError: error => errorMessages.pushApiError(error),
		onSuccess: () => {
			toast.success('Released — this post will send as scheduled.');
			return campaign?.load()?.catch(error => errorMessages.pushApiError(error));
		},
	});

	// eslint-disable-next-line react-hooks/rules-of-hooks
	const scheduleDate = campaign instanceof CampaignViewModel ? campaign.schedule?.startDate : campaign?.dueDate;
	const renderSendOnBehalf = () => {
		let sendFrom: string;
		if (emailComposer) {
			sendFrom = !emailComposer?.emailMessage?.options?.sendEmailFrom
				? getDisplayName(userSession.user)
				: emailComposer?.emailMessage?.isSendFromOption(SendEmailFrom.SelectedUser) &&
						!!emailComposer.emailMessage.options?.sendEmailFromUser
					? getDisplayName(emailComposer.emailMessage.options.sendEmailFromUser)
					: null;
		} else if (campaign?.actor && campaign.actor.id !== campaign.creator.id) {
			sendFrom = campaign.actor.name;
		}

		if (!sendFrom) {
			return null;
		}
		return (
			<div className={css(styleSheet.card)}>
				<div className={css(styleSheet.cardTitle)}>Send From</div>
				<div>{sendFrom}</div>
			</div>
		);
	};

	const approveCampaign = async () => {
		if (onApproveClicked) {
			onApproveClicked();
			return;
		}
		if (campaign instanceof SocialMediaPostViewModel) {
			try {
				await campaign.approve();

				toast.success(`Social media post approved`);
				postNotification({
					info: campaign,
					topic: Topics.EDIT_SOCIAL_POST,
				});
				fullscreenModal?.dismissModal();
			} catch (error) {
				logApiError('ApproveSocialMediaPost-Error', error);
				errorMessages.pushApiError(error);
			}
		}
	};

	const approveCampaignWithoutCompliance = async () => {
		if (onApproveWithoutCompliance) {
			onApproveWithoutCompliance();
			return;
		}

		if (campaign instanceof SocialMediaPostViewModel) {
			try {
				await campaign.update({ ...campaign.toJs(), sendWithCompliance: false });
				await campaign.approve();
				toast.success(`Social media post approved without compliance`);
				postNotification({
					info: campaign,
					topic: Topics.EDIT_SOCIAL_POST,
				});
				fullscreenModal?.dismissModal();
			} catch (error) {
				logApiError('ApproveSocialMediaPost-Error', error);
				errorMessages.pushApiError(error);
			}
		}
	};

	const cancelClicked = (e?: React.MouseEvent<HTMLElement>) => {
		if (onCancelClicked) {
			onCancelClicked(e);
			return;
		}
		if (campaign instanceof SocialMediaPostViewModel) {
			setShowingCancelConfirmation(true);
		}
	};

	// Recipient is resolved and validated by <ComplianceSendActions>: an explicit typed address, or `null`
	// for the self/client copy so the API routes to its default recipient (the URL is constructed differently
	// for the null case in the underlying send methods).
	const sendCompliance = async (complianceEmail: string | null) => {
		if (onSendCompliance) {
			// Preserve the original recipient semantics: `null` routes to the API default (strictNullChecks is
			// off, so this satisfies the optional `string` parameter).
			onSendCompliance(complianceEmail);
			return;
		}

		if (campaign instanceof SocialMediaPostViewModel) {
			const vm = new SocialAccountCampaignViewModel(userSession, campaign.toJs());
			if (impersonationContext?.isValid) {
				vm.impersonate({
					account: impersonationContext?.account,
					user: campaign.creator,
				});
			}
			try {
				await (vm.sendWithCompliance ? vm.sendComplianceApprovalRequest(complianceEmail) : vm.sendApprovalRequest());
				logEvent('SocialMediaPostNotificationPendingPostCopySend', { complianceEmail });
				toast.success(`Copy sent to ${complianceEmail ? complianceEmail : "client's email address"}`);
			} catch (error) {
				toast.error('Failed to send copy');
				logApiError('SocialMediaPostNotificationPendingPostCopySend-Error', error);
				errorMessages.pushApiError(error);
			}
		}
	};

	/**
	 * Both revise endpoints declare `requiresUser`, which is a route constraint — an account-only
	 * context omits `impUserId` and the route simply does not match, giving a 404 rather than an error
	 * that says why. Impersonating as the creator also satisfies the API's own creator-or-admin gate.
	 */
	const reviseImpersonationContext = () => ImpersonationBroker.withForUser(impersonationContext, campaign?.creator?.id);

	const { isRetryingRevision, onRetryRevisionClicked, revisionOwed, runRevisionFiling } = useRegEdRevisionFiling({
		getImpersonationContext: reviseImpersonationContext,
		logApiError,
		onRevisionFiled: async () => {
			await campaign?.load();
		},
		parentId: campaign?.id,
		parentType: RegEdComplianceContentType.Email,
		userSession,
		regenerateCompliancePdf: (impersonation?: IImpersonationContext) =>
			regEdRegeneratePdf.mutateAsync({ campaignId: campaign.id, impersonationContext: impersonation }),
	});
	const effectiveRevisionOwed = hostRevisionOwed !== undefined ? hostRevisionOwed : revisionOwed;
	const effectiveIsRetryingRevision = hostIsRetryingRevision ?? isRetryingRevision;

	/**
	 * Revising edits the campaign in place, in four calls whose order the API does not enforce:
	 * save the body (`revise` snapshots it, so an unsaved edit files what the reviewer already turned
	 * down), reopen the round, repoint the RegEd submission onto that round, then render the PDF —
	 * which is what actually files. `RegEdCompliancePdfGeneratedHandler` resolves the submission by the
	 * new round's id and gives up if it is missing, so skipping the repoint submits nothing and says so
	 * to no one.
	 */
	const onReviseConfirmed = async (submission: IRegEdSubmissionView) => {
		setIsRevising(true);
		try {
			// Both failure shapes have to be caught: continuing on an unsaved body would let `revise`
			// snapshot the version the reviewer already turned down.
			// The composer outlives this dialog, so an unsaved schedule left on it would be committed by
			// some later unrelated save. Restored on every path that does not complete the save.
			const priorScheduledSend = emailComposer?.emailMessage?.options?.scheduledSend;
			const restoreScheduledSend = () => {
				if (reviseSendDate && emailComposer?.emailMessage?.options) {
					emailComposer.emailMessage.options.scheduledSend = priorScheduledSend;
				}
			};
			try {
				if (reviseSendDate && emailComposer?.emailMessage?.options) {
					// Rides along with the save — updateCampaign spreads options.scheduledSend into the PUT —
					// so the new date needs no call of its own. `revise` forces the criteria to OnHold after.
					emailComposer.emailMessage.options.scheduledSend = {
						...priorScheduledSend,
						startDate: reviseSendDate.toISOString(),
					};
				}
				const saveResult = await emailComposer?.updateCampaign();
				// Resolving with no value is the composer-busy case; a real save error rejects instead.
				if (!saveResult?.success) {
					restoreScheduledSend();
					toast.error('We could not save your changes, so the campaign was not sent back for review.');
					return;
				}
			} catch (error) {
				restoreScheduledSend();
				logApiError('RegEdEmailReviseSave-Error', error);
				errorMessages.pushApiError(error);
				toast.error('We could not save your changes, so the campaign was not sent back for review.');
				return;
			}
			const revised = await regEdEmailRevise.mutateAsync({
				campaignId: campaign.id,
				impersonationContext: reviseImpersonationContext(),
			});
			const raisedRound = findRaisedRegEdRound(revised?.approvalRequests);
			if (!raisedRound?.id) {
				// Nothing to file against. Returning here rather than parking on the retry card, which would
				// replay the same empty call and never succeed.
				logApiError('RegEdEmailRevise-NoRaisedRound');
				toast.error(
					'The review was reopened, but we could not tell which round to file against. Reopen the campaign and try again.'
				);
				return;
			}
			await runRevisionFiling(raisedRound.id, submission, true);
		} catch (error) {
			logApiError('RegEdEmailRevise-Error', error);
		} finally {
			setIsRevising(false);
		}
	};

	const handleReviseCompliance = (submission: IRegEdSubmissionView) => {
		if (!(campaign instanceof CampaignViewModel)) {
			return;
		}
		// The dialog does not stay mounted, so a draft the advisor walked away from must not outrank the
		// round's real filing on the next open.
		setReviseDraft(null);
		setPendingRevision(submission);
		setReviseSendDate(campaign.schedule?.startDate ? new Date(campaign.schedule.startDate) : null);
		setReviseCarriedFrom(null);
		reviseCarryPending.current = true;
		setPickingReviseDate(false);
		setShowingReviseConfirmation(true);
	};

	// The revision's review date is refreshed to the firm's notice period and first use follows the send, so a
	// send that has passed — or lands inside that period — would file short notice the advisor never chose.
	const { data: reviseSchema, isLoading: reviseSchemaLoading } = useRegEdMetadataSchemaQuery(
		reviseDraft?.format || pendingRevision?.metadata?.format || null,
		{ enabled: showingReviseConfirmation, impersonationContext }
	);
	const reviseSendEmail =
		impersonationContext?.account?.features?.sendEmail ?? userSession?.account?.features?.sendEmail;
	React.useEffect(() => {
		if (!showingReviseConfirmation || !reviseCarryPending.current || !reviseSendDate || reviseSchemaLoading) {
			return;
		}
		reviseCarryPending.current = false;
		const carried = carrySendForward(
			reviseSendDate,
			findReviewMinimumDate(reviseSchema),
			getNonSendingDaysOfWeek(reviseSendEmail)
		);
		if (carried.getTime() !== reviseSendDate.getTime()) {
			setReviseCarriedFrom(reviseSendDate);
			setReviseSendDate(carried);
		}
	}, [showingReviseConfirmation, reviseSendDate, reviseSchema, reviseSchemaLoading, reviseSendEmail]);

	// Must stay below every hook: the fullscreen modal's close transition re-renders this with no campaign, and an
	// earlier return would change the hook count mid-life and crash the page.
	if (!campaign) {
		return null;
	}

	const handleReviseSocialCompliance = (submission: IRegEdSubmissionView) => {
		if (!(campaign instanceof SocialMediaPostViewModel)) {
			return;
		}
		if (onReviseCompliance) {
			setReviseDraft(null);
			setPendingRevision(submission);
			setShowingReviseConfirmation(true);
			return;
		}
		// Hosts without a save still seed a fresh post; the editor supersedes in place instead.
		history.push(buildRegEdSocialReviseLocation(campaign, submission));
	};

	const regEdConditionsPending = getRegEdConditionsPending(campaign.approvalRequests);

	const handleReleaseConditions = (_: IRegEdSubmissionView) => {
		if (campaign instanceof CampaignViewModel) {
			// A "send now" held campaign persists with no startDate; StartAfter without one is a server 400.
			const schedule = {
				...campaign.schedule,
				criteria: campaign.schedule?.startDate ? ScheduleCriteria.StartAfter : ScheduleCriteria.Immediately,
			};
			regEdEmailRelease.mutate({ campaignId: campaign.id, schedule, impersonationContext });
			return;
		}
		regEdSocialRelease.mutate({ postId: campaign.id, dueDate: campaign.dueDate, impersonationContext });
	};

	const onCancelConfirmationRequestClose = (result?: IConfirmationDialogOption<boolean>, canceled?: boolean) => {
		if (!canceled && result?.representedObject) {
			cancelCampaign();
		}
		setShowingCancelConfirmation(false);
	};

	const cancelCampaign = async () => {
		if (campaign instanceof SocialMediaPostViewModel) {
			try {
				logEvent('CancelSocialCampaign', { id: campaign?.id });

				await campaign.cancel();

				toast.success(campaign?.status === PostStatus.Pending ? `Pending post cancelled` : 'Post cancelled');
				postNotification({
					info: campaign,
					topic: Topics.EDIT_SOCIAL_POST,
				});
				fullscreenModal?.history?.goBack();
			} catch (error) {
				logApiError('CancelSocialPendingCampaign-Error', error);
				errorMessages.pushApiError(error);
			}
		}
	};

	const onRejectionConfirmationRequestClose = (result?: IConfirmationDialogOption<boolean>, canceled?: boolean) => {
		if (campaign instanceof SocialMediaPostViewModel) {
			if (!canceled && !!result?.representedObject) {
				const promise = campaign.reject();
				if (promise) {
					logEvent('RejectCampaign');
					promise
						.then(() => {
							toast.success(`Campaign rejected`);
							postNotification({
								info: campaign,
								topic: Topics.EDIT_SOCIAL_POST,
							});
							fullscreenModal?.dismissModal();
						})
						.catch((error: IOperationResultNoValue) => {
							logApiError(`RejectCampaign-Error`);
							errorMessages.pushApiError(error);
						});
				}
			}
		}
		setShowingCancelConfirmation(false);
	};

	const renderCancelConfirmationDialog = () => (
		<ConfirmationDialog
			icon={<WarningIcon />}
			modalProps={{
				isOpen: showingCancelConfirmation,
				onRequestClose:
					!impersonationContext && campaign?.status === PostStatus.Pending
						? onRejectionConfirmationRequestClose
						: onCancelConfirmationRequestClose,
			}}
			options={getCancelConfirmationOptions(
				!impersonationContext && campaign?.status === PostStatus.Pending ? 'Reject' : 'Cancel Campaign',
				'Nevermind'
			)}
			title={`Are you sure you want to ${
				!impersonationContext && campaign?.status === PostStatus.Pending ? 'reject' : 'cancel'
			} this social media campaign?`}
		/>
	);

	/**
	 * The questionnaire is editable on a revision, not replayed: a reviewer can reject *because of* the
	 * form, and the requested review date inside the answers goes stale between rounds. Seeded from the
	 * prior filing so the advisor edits it rather than re-entering it.
	 */
	// Email only: a post carries `dueDate`, not `schedule.startDate`.
	const reviseNeedsSendDate = campaign instanceof CampaignViewModel;
	const reviseSendDateBlocksApproval =
		reviseNeedsSendDate && getRegEdReviseDateState('email', reviseSendDate).blocksApproval;

	const renderReviseConfirmationDialog = () => (
		<Dialog
			open={showingReviseConfirmation}
			onOpenChange={open => {
				if (!effectiveIsRevising) {
					setShowingReviseConfirmation(open);
				}
			}}
		>
			<DialogPopup showCloseButton={!effectiveIsRevising}>
				<DialogHeader>
					<DialogTitle>Revise and resubmit for compliance review</DialogTitle>
					<DialogDescription>
						{`Your edits are saved and the reviewer sees the ${
							campaign instanceof CampaignViewModel ? 'campaign' : 'post'
						} again. Update the compliance details if the reviewer asked you to.`}
					</DialogDescription>
				</DialogHeader>
				{pickingReviseDate ? (
					<CampaignDateTimePicker
						ctaText='Select'
						// Without these the picker short-circuits its minimum to now, letting the advisor choose a
						// time inside the firm's compliance window or the send-on-behalf wait.
						impersonationContext={props.impersonationContext}
						sendFrom={emailComposer?.emailMessage?.options?.sendEmailFrom}
						sendFromUserId={emailComposer?.emailMessage?.options?.sendEmailFromUser?.id}
						initialValue={reviseSendDate ?? new Date()}
						onCancel={() => setPickingReviseDate(false)}
						onSave={date => {
							setReviseSendDate(date);
							setReviseCarriedFrom(null);
							setPickingReviseDate(false);
						}}
					/>
				) : (
					<>
						{reviseNeedsSendDate && reviseSendDate ? (
							<>
								<ScheduleDate
									disabled={effectiveIsRevising}
									onClick={() => setPickingReviseDate(true)}
									scheduleDate={reviseSendDate.toISOString()}
									showChangeButton={true}
									title='Send Time'
								/>
								<RegEdReviseDateNotice carriedFrom={reviseCarriedFrom} date={reviseSendDate} kind='email' />
							</>
						) : null}
						<RegEdComplianceApprovalPrompt
							campaignType={campaign instanceof CampaignViewModel ? CampaignType.Email : CampaignType.Social}
							impersonationContext={impersonationContext}
							initialAnswers={reviseDraft?.answers ?? pendingRevision?.metadata?.answers}
							initialFormat={reviseDraft?.format ?? pendingRevision?.metadata?.format}
							isRevision
							revisionSendDate={reviseNeedsSendDate ? reviseSendDate : null}
							onFormStateChange={setReviseDraft}
							disabled={reviseSendDateBlocksApproval}
							isLoading={effectiveIsRevising}
							onScheduleRegEdCompliance={async (_, metadata) => {
								if (!pendingRevision) {
									return;
								}
								const revision = { ...pendingRevision, metadata };
								try {
									// The sequence is the host's when it owns the save; the questionnaire stays with the button.
									await (onReviseCompliance ? onReviseCompliance(revision) : onReviseConfirmed(revision));
								} finally {
									// Held open for the duration so the in-flight guards above actually hold the advisor;
									// failures surface as a toast or the retry card behind it.
									setShowingReviseConfirmation(false);
								}
							}}
							styles={[styleSheet.reviseDialogForm]}
							submitLabel='Revise & Resubmit'
						/>
					</>
				)}
			</DialogPopup>
		</Dialog>
	);

	const renderActions = () => {
		if (campaignGroup) {
			return (
				<div className={css(styleSheet.actions, styleSheet.cancelInstructions)}>
					Please go to the Email Reports section to cancel or notify client
				</div>
			);
		}
		if (
			(campaign instanceof CampaignViewModel && !campaign.isScheduledToSend) ||
			(campaign instanceof SocialMediaPostViewModel && !campaign?.hasEditableStatus)
		) {
			return null;
		}
		const showApproveButton =
			!usesRegEd &&
			!(props.impersonationContext?.isValid || accountHasComplianceEnabled(userSession, impersonationContext)) &&
			(campaign instanceof CampaignViewModel ||
				(campaign instanceof SocialMediaPostViewModel && (campaign?.canEdit || campaign?.canEditAsAdmin)));
		return (
			<div
				className={css(
					campaign instanceof CampaignViewModel && campaign.status === EmailSendStatus.WaitingForApproval
						? styleSheet.actions
						: styleSheet.actionsVertical
				)}
			>
				{campaign?.status === EmailSendStatus.Queued && !!sendTestEmail && (
					<ButtonV2 variant='link' onClick={() => setShowingTestEmailModal(true)}>
						<PaperPlaneIcon />
						Send a Test
					</ButtonV2>
				)}
				{(campaign instanceof CampaignViewModel && campaign.status === EmailSendStatus.WaitingForApproval) ||
				(campaign instanceof SocialMediaPostViewModel &&
					campaign.status === PostStatus.Pending &&
					(campaign.canEdit || campaign.canEditAsAdmin || campaign.impersonationContext)) ? (
					<>
						{showApproveButton && (
							<Button
								className={css(baseStyleSheet.ctaButton)}
								disabled={disabled || (campaign.isLoaded && campaign.isBusy)}
								onClick={approveCampaign}
								label='Approve Campaign'
								isLoading={campaign.isLoaded && campaign.isBusy}
							/>
						)}
						<ButtonV2
							variant='destructive-secondary'
							disabled={campaign.isLoaded && campaign.isBusy}
							onClick={cancelClicked}
						>
							{props.impersonationContext?.isValid ? 'Cancel this Campaign' : 'Reject this Campaign'}
						</ButtonV2>
					</>
				) : (campaign instanceof SocialMediaPostViewModel && campaign.hasEditableStatus) ||
					(campaign instanceof CampaignViewModel && !readOnly) ? (
					<Button
						className={css(baseStyleSheet.linkDestructive)}
						disabled={campaign.isLoaded && campaign.isBusy}
						onClick={cancelClicked}
						label='Cancel Campaign'
						kind='custom'
					/>
				) : null}
			</div>
		);
	};

	const IsSendOnBehalfFromUser =
		campaign instanceof SocialMediaPostViewModel &&
		campaign?.actor &&
		campaign.actor.id !== campaign.creator.id &&
		!(campaign?.actor.isLevitateSupport && campaign?.approvalRequests);

	// RegEd files regardless of the legacy `enabled` flag, so pending-ness cannot be read from it.
	const complianceApplies = accountHasComplianceApplied(userSession, impersonationContext, usesRegEd);

	const emailComplianceActions =
		campaign instanceof CampaignViewModel &&
		campaign.status === EmailSendStatus.WaitingForApproval &&
		complianceApplies &&
		emailComposer.emailMessage.options?.sendWithCompliance === true;

	const socialComplianceActions =
		campaign instanceof SocialMediaPostViewModel &&
		campaign.status === PostStatus.Pending &&
		(campaign?.canEdit || campaign?.canEditAsAdmin || props.impersonationContext?.isValid) &&
		complianceApplies;

	const sentWithoutWaiting =
		campaign?.approvalRequests?.length > 0 &&
		(campaign instanceof CampaignViewModel
			? campaign.status === EmailSendStatus.Queued && emailComposer?.emailMessage?.options?.sendWithCompliance === false
			: (campaign.status === PostStatus.Scheduled ||
					campaign.status === PostStatus.Succeeded ||
					campaign.status === PostStatus.PartiallySucceeded) &&
				campaign.sendWithCompliance === false);

	const canSendWithoutCompliance =
		(emailComplianceActions || socialComplianceActions) &&
		!impersonationContext?.isValid &&
		accountAllowsComplianceBypass(userSession, impersonationContext, usesRegEd) &&
		!(socialComplianceActions && !campaign?.canEdit);

	return (
		<div
			className={`${css(
				campaign instanceof CampaignViewModel ? styleSheet.container : styleSheet.socialContainer,
				...(styles || [])
			)} campaign-details-summary ${className || ''}`}
		>
			{!!sendTestEmail && (
				<TestEmailModal
					isOpen={showingTestEmailModal}
					onRequestClose={onTestEmailModalClose}
					onSendTestEmail={sendTestEmail}
				/>
			)}
			{campaign instanceof CampaignViewModel ? (
				<>
					{renderSendOnBehalf()}
					<div className={css(styleSheet.card, styleSheet.recipientsCard)}>
						<div className={css(styleSheet.cardTitle)}>Recipients</div>
						<div className={css(styleSheet.cardActions)}>
							<div className={css(baseStyleSheet.truncateText)}>
								{`Approximately ${
									!campaignGroup ? campaign.totalCount : campaignGroup.reduce((x, camp) => x + camp.totalCount, 0)
								}`}
							</div>
							{!readOnly && !campaignGroup && (
								<button className={css(baseStyleSheet.brandLink)} onClick={onEditRecipientsClicked}>
									<span>{`Preview${!impersonationContext?.isValid && !emailComposer?.isFollowUp ? '/Edit' : ''}`}</span>
								</button>
							)}
						</div>
						<TagEmailTagList
							campaign={campaign}
							emailComposer={emailComposer}
							readOnly={true}
							styles={[styleSheet.tagList]}
						/>
					</div>
				</>
			) : null}
			{scheduleDate ? (
				<>
					<ScheduleDate
						title={campaign instanceof CampaignViewModel ? 'Send Time' : 'Posting Date'}
						scheduleDate={scheduleDate}
						showChangeButton={
							(campaign instanceof CampaignViewModel &&
								campaign?.isScheduledToSend &&
								!campaignGroup &&
								!emailComposer?.editModeIsActive) ||
							(campaign instanceof SocialMediaPostViewModel &&
								(campaign?.canEdit || (campaign?.impersonationContext && campaign?.hasEditableStatus)))
						}
						disabled={disabled}
						onClick={onChangeScheduleClicked}
					/>
				</>
			) : null}
			{IsSendOnBehalfFromUser ? <ScheduleBy name={campaign?.actor?.name} /> : null}
			{usesRegEd ? (
				<RegEdComplianceStatus
					parentId={campaign.id}
					impersonationContext={impersonationContext}
					onRevise={campaign instanceof CampaignViewModel ? handleReviseCompliance : handleReviseSocialCompliance}
					conditionsPending={regEdConditionsPending}
					onReleaseConditions={handleReleaseConditions}
					isReleasing={regEdEmailRelease.isPending || regEdSocialRelease.isPending}
					isRevising={effectiveIsRevising}
					reviseDisabled={!!effectiveRevisionOwed}
					onSendWithoutCompliance={canSendWithoutCompliance ? approveCampaignWithoutCompliance : undefined}
					disabled={disabled}
					sentWithoutWaiting={sentWithoutWaiting}
					sentWithoutWaitingBy={campaign?.approvalRequests?.[0]?.approver?.firstName}
					sendWithoutComplianceLabel={emailComplianceActions ? 'Send Anyway' : 'Post Anyway'}
				/>
			) : null}
			{effectiveRevisionOwed ? (
				<RegEdRevisionOwedCard
					isRetrying={effectiveIsRetryingRevision}
					needsFiling={effectiveRevisionOwed.needsFiling}
					onRetry={hostOnRetryRevisionClicked ?? onRetryRevisionClicked}
				/>
			) : null}
			{!usesRegEd && (emailComplianceActions || socialComplianceActions) && (
				<ComplianceSendActions
					accountComplianceEmail={getAccountComplianceEmail(userSession, impersonationContext)}
					isImpersonating={!!props.impersonationContext?.isValid}
					selfEmail={userSession.user.email}
					disabled={disabled}
					onSend={sendCompliance}
					// Email hides the send actions while editing or for grouped/unscheduled campaigns; social
					// always shows them. Coerce to a real boolean so the component default never sneaks in.
					showSendActions={
						!!(
							(emailComplianceActions &&
								campaign.isScheduledToSend &&
								!campaignGroup &&
								!emailComposer.editModeIsActive) ||
							socialComplianceActions
						)
					}
					footer={
						!props.impersonationContext?.isValid ? (
							<div className={css(styleSheet.buttonActions)}>
								<div className={css(baseStyleSheet.horizontalStack)}>
									{emailComposer?.campaign?.emailBodyTemplate?.citation?.length > 0 && (
										<button className={css(baseStyleSheet.ctaButtonReverse)} onClick={onViewCitationsClicked}>
											<span>View Citations</span>
										</button>
									)}
									<button className={css(baseStyleSheet.ctaButton)} disabled={disabled} onClick={approveCampaign}>
										<span>Mark as Approved</span>
									</button>
									{canSendWithoutCompliance ? (
										<ButtonV2 variant='secondary' disabled={disabled} onClick={approveCampaignWithoutCompliance}>
											{emailComplianceActions ? 'Send Anyway' : 'Post Anyway'}
										</ButtonV2>
									) : null}
								</div>
							</div>
						) : null
					}
				/>
			)}
			{!usesRegEd &&
			campaign instanceof SocialMediaPostViewModel &&
			campaign.status === PostStatus.Pending &&
			!socialComplianceActions &&
			campaign?.approvalRequests?.length > 0 ? (
				<ApprovalStatus title='Pending Approval' />
			) : null}
			{!usesRegEd &&
				campaign instanceof CampaignViewModel &&
				campaign.status === EmailSendStatus.Queued &&
				accountHasComplianceEnabled(userSession, impersonationContext) &&
				campaign.approvalRequests?.length > 0 && (
					<div className={css(styleSheet.card)}>
						<div className={css(styleSheet.cardTitle)}>Compliance Approval</div>
						<div className={css(styleSheet.cardActions)}>
							{emailComposer.emailMessage.options?.sendWithCompliance ? (
								<div className={css(styleSheet.cardActions)}>
									<div className={css(styleSheet.approvedStatusTitle)}>Approved &nbsp;</div>
									<div className={css(styleSheet.approvedStatusDate)}>
										{getDefaultDateStringValue(campaign.approvalRequests[0].lastModifiedDate)}
									</div>
								</div>
							) : (
								<div className={css(styleSheet.approvedStatusTitle)}>
									{'Sent By ' + campaign.approvalRequests[0]?.approver?.firstName + ' Anyway'}
								</div>
							)}
						</div>
					</div>
				)}
			{!usesRegEd &&
				campaign instanceof SocialMediaPostViewModel &&
				(campaign.status === PostStatus.Scheduled ||
					campaign.status === PostStatus.Succeeded ||
					campaign.status === PostStatus.PartiallySucceeded) &&
				accountHasComplianceEnabled(userSession, impersonationContext) && (
					<div className={css(styleSheet.card)}>
						<div className={css(styleSheet.cardTitle)}>Compliance Approval</div>
						<div className={css(styleSheet.cardActions)}>
							{campaign?.sendWithCompliance ? (
								<div className={css(styleSheet.cardActions)}>
									<div className={css(styleSheet.approvedStatusTitle)}>Approved &nbsp;</div>
									<div className={css(styleSheet.approvedStatusDate)}>
										{campaign?.approvalRequests &&
											getDefaultDateStringValue(campaign?.approvalRequests[0]?.lastModifiedDate)}
									</div>
								</div>
							) : campaign?.approvalRequests?.length > 0 ? (
								<div className={css(styleSheet.approvedStatusTitle)}>
									{'Scheduled By ' + campaign?.approvalRequests[0].approver?.firstName + ' Anyway'}
								</div>
							) : (
								<div className={css(styleSheet.approvedStatusTitle)}>Scheduled Without Compliance</div>
							)}
						</div>
					</div>
				)}

			{renderActions()}
			{renderCancelConfirmationDialog()}
			{renderReviseConfirmationDialog()}
		</div>
	);
};

const CampaignDetailsSummaryAsObserver = observer(CampaignDetailsSummaryFc);
export const CampaignDetailsSummary = inject(
	UserSessionViewModelKey,
	ImpersonationContextKey
)(CampaignDetailsSummaryAsObserver);
